import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchRegistrations } from '../utils/sharedInbox';

/* --------------------------------- Models --------------------------------- */

export interface RegistrationRecord {
  id: string;
  reference: string;
  fullName: string;
  email: string;
  professionalTitle: string;
  yearsExperience: string;
  linkedInUrl: string;
  motivation: string;
  submittedAt: string;
  reviewed: boolean;
}
export type ActivityKind = 'registration' | 'assistant' | 'message' | 'job' | 'assignment' | 'system';
export interface ActivityRecord {
  id: string;
  kind: ActivityKind;
  summary: string;
  detail?: string;
  occurredAt: string;
}
export interface Message {
  id: string;
  from: 'client' | 'owner';
  body: string;
  sentAt: string;
}
export interface MessageThread {
  id: string;
  reference: string;
  /** Who started the conversation. */
  contactName: string;
  contactEmail: string;
  organization: string;
  subject: string;
  messages: Message[];
  status: 'open' | 'closed';
  unreadForOwner: boolean;
  /** 'owner' when we reached out first. Existing threads default to 'client'. */
  initiatedBy?: 'client' | 'owner';
  /** Why we reached out — set on owner-initiated threads only. */
  purpose?: OutreachPurpose;
  /** Whether the message has actually been sent from your email client yet. */
  dispatched?: boolean;
  createdAt: string;
  updatedAt: string;
}
export type OutreachPurpose = 'invite_to_register' | 'introduce_mandate' | 'general';
export type JobStatus = 'draft' | 'open' | 'closed';
export interface JobDescription {
  id: string;
  reference: string;
  title: string;
  organization: string;
  location: string;
  employmentType: string;
  compensation: string;
  summary: string;
  responsibilities: string;
  requirements: string;
  status: JobStatus;
  createdAt: string;
  updatedAt: string;
}
export type AssignmentStatus = 'invited' | 'viewed' | 'applied' | 'declined';
export interface Assignment {
  id: string;
  jobId: string;
  registrationId: string;
  candidateName: string;
  candidateEmail: string;
  note: string;
  status: AssignmentStatus;
  assignedAt: string;
}

/* -------------------------------- Contract -------------------------------- */

interface RegistryValue {
  registrations: RegistrationRecord[];
  activity: ActivityRecord[];
  threads: MessageThread[];
  jobs: JobDescription[];
  assignments: Assignment[];
  addRegistration: (input: Omit<RegistrationRecord, 'id' | 'reference' | 'submittedAt' | 'reviewed'>) => RegistrationRecord;
  toggleReviewed: (id: string) => void;
  removeRegistration: (id: string) => void;
  startThread: (input: {
    contactName: string;
    contactEmail: string;
    organization: string;
    subject: string;
    body: string;
  }) => MessageThread;
  /** Owner-initiated outreach to someone who has not written in. */
  startOutreach: (input: {
    contactName: string;
    contactEmail: string;
    organization: string;
    subject: string;
    body: string;
    purpose: OutreachPurpose;
  }) => MessageThread;
  markDispatched: (threadId: string) => void;
  replyToThread: (threadId: string, body: string, from?: 'client' | 'owner') => void;
  markThreadRead: (threadId: string) => void;
  setThreadStatus: (threadId: string, status: MessageThread['status']) => void;
  saveJob: (input: Omit<JobDescription, 'id' | 'reference' | 'createdAt' | 'updatedAt'> & {
    id?: string;
  }) => JobDescription;
  setJobStatus: (jobId: string, status: JobStatus) => void;
  removeJob: (jobId: string) => void;
  assignJob: (jobId: string, registrationId: string, note: string) => void;
  setAssignmentStatus: (assignmentId: string, status: AssignmentStatus) => void;
  removeAssignment: (assignmentId: string) => void;
  logActivity: (kind: ActivityKind, summary: string, detail?: string) => void;
  clearActivity: () => void;

  /** Re-reads stored records — picks up anything submitted in another tab. */
  refresh: () => void;
  lastRefreshedAt: string;

  /** Pulls registrations submitted on other devices into local records. */
  syncFromInbox: () => Promise<{
    added: number;
    found: number;
    reachable: boolean;
    error?: string;
  }>;
}
const STORAGE_KEY = 'velnoris.registry.v2';
const RegistryContext = createContext<RegistryValue | null>(null);
interface Persisted {
  registrations: RegistrationRecord[];
  activity: ActivityRecord[];
  threads: MessageThread[];
  jobs: JobDescription[];
  assignments: Assignment[];
}
const empty: Persisted = {
  registrations: [],
  activity: [],
  threads: [],
  jobs: [],
  assignments: []
};
function load(): Persisted {
  if (typeof window === 'undefined') return empty;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return {
      registrations: parsed.registrations ?? [],
      activity: parsed.activity ?? [],
      threads: parsed.threads ?? [],
      jobs: parsed.jobs ?? [],
      assignments: parsed.assignments ?? []
    };
  } catch {
    return empty;
  }
}
const newId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const ref = (prefix: string) => `${prefix}-2026-${String(Math.floor(1000 + Math.random() * 9000))}`;
export function RegistryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<Persisted>(() => load());
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => new Date().toISOString());
  const refresh = useCallback(() => {
    setState(load());
    setLastRefreshedAt(new Date().toISOString());
  }, []);

  /* Pick up submissions made in another tab of this browser as they happen. */
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage unavailable — the console still works for this session */
    }
  }, [state]);
  const event = (kind: ActivityKind, summary: string, detail?: string): ActivityRecord => ({
    id: newId('act'),
    kind,
    summary,
    detail,
    occurredAt: new Date().toISOString()
  });
  const logActivity = useCallback<RegistryValue['logActivity']>((kind, summary, detail) => {
    setState(prev => ({
      ...prev,
      activity: [event(kind, summary, detail), ...prev.activity].slice(0, 400)
    }));
  }, []);

  /* ----------------------------- Registrations ---------------------------- */

  const addRegistration = useCallback<RegistryValue['addRegistration']>(input => {
    const record: RegistrationRecord = {
      ...input,
      id: newId('reg'),
      reference: ref('REG'),
      submittedAt: new Date().toISOString(),
      reviewed: false
    };
    setState(prev => ({
      ...prev,
      registrations: [record, ...prev.registrations],
      activity: [event('registration', `New registration — ${record.fullName}`, `${record.email}${record.professionalTitle ? ` · ${record.professionalTitle}` : ''} · ${record.reference}`), ...prev.activity].slice(0, 400)
    }));
    return record;
  }, []);
  const toggleReviewed = useCallback<RegistryValue['toggleReviewed']>(id => {
    setState(prev => ({
      ...prev,
      registrations: prev.registrations.map(r => r.id === id ? {
        ...r,
        reviewed: !r.reviewed
      } : r)
    }));
  }, []);
  const removeRegistration = useCallback<RegistryValue['removeRegistration']>(id => {
    setState(prev => ({
      ...prev,
      registrations: prev.registrations.filter(r => r.id !== id)
    }));
  }, []);

  /* -------------------------------- Messages ------------------------------- */

  const startThread = useCallback<RegistryValue['startThread']>(input => {
    const now = new Date().toISOString();
    const thread: MessageThread = {
      id: newId('thr'),
      reference: ref('MSG'),
      contactName: input.contactName,
      contactEmail: input.contactEmail,
      organization: input.organization,
      subject: input.subject,
      messages: [{
        id: newId('msg'),
        from: 'client',
        body: input.body,
        sentAt: now
      }],
      status: 'open',
      unreadForOwner: true,
      createdAt: now,
      updatedAt: now
    };
    setState(prev => ({
      ...prev,
      threads: [thread, ...prev.threads],
      activity: [event('message', `New message — ${thread.contactName}`, `${thread.subject} · ${thread.contactEmail} · ${thread.reference}`), ...prev.activity].slice(0, 400)
    }));
    return thread;
  }, []);
  const startOutreach = useCallback<RegistryValue['startOutreach']>(input => {
    const now = new Date().toISOString();
    const thread: MessageThread = {
      id: newId('thr'),
      reference: ref('OUT'),
      contactName: input.contactName,
      contactEmail: input.contactEmail,
      organization: input.organization,
      subject: input.subject,
      messages: [{
        id: newId('msg'),
        from: 'owner',
        body: input.body,
        sentAt: now
      }],
      status: 'open',
      unreadForOwner: false,
      initiatedBy: 'owner',
      purpose: input.purpose,
      dispatched: false,
      createdAt: now,
      updatedAt: now
    };
    setState(prev => ({
      ...prev,
      threads: [thread, ...prev.threads],
      activity: [event('message', `Outreach prepared — ${thread.contactName}`, `${thread.subject} · ${thread.contactEmail} · ${thread.reference}`), ...prev.activity].slice(0, 400)
    }));
    return thread;
  }, []);
  const markDispatched = useCallback<RegistryValue['markDispatched']>(threadId => {
    setState(prev => ({
      ...prev,
      threads: prev.threads.map(t => t.id === threadId ? {
        ...t,
        dispatched: true
      } : t),
      activity: [event('message', `Outreach sent — ${prev.threads.find(t => t.id === threadId)?.contactName ?? 'contact'}`), ...prev.activity].slice(0, 400)
    }));
  }, []);
  const replyToThread = useCallback<RegistryValue['replyToThread']>((threadId, body, from = 'owner') => {
    const now = new Date().toISOString();
    setState(prev => ({
      ...prev,
      threads: prev.threads.map(t => t.id === threadId ? {
        ...t,
        messages: [...t.messages, {
          id: newId('msg'),
          from,
          body,
          sentAt: now
        }],
        unreadForOwner: from === 'client',
        updatedAt: now
      } : t),
      activity: from === 'owner' ? prev.activity : [event('message', 'Client replied to a thread', body.slice(0, 120)), ...prev.activity].slice(0, 400)
    }));
  }, []);
  const markThreadRead = useCallback<RegistryValue['markThreadRead']>(threadId => {
    setState(prev => ({
      ...prev,
      threads: prev.threads.map(t => t.id === threadId ? {
        ...t,
        unreadForOwner: false
      } : t)
    }));
  }, []);
  const setThreadStatus = useCallback<RegistryValue['setThreadStatus']>((threadId, status) => {
    setState(prev => ({
      ...prev,
      threads: prev.threads.map(t => t.id === threadId ? {
        ...t,
        status
      } : t)
    }));
  }, []);

  /* ---------------------------------- Jobs --------------------------------- */

  const saveJob = useCallback<RegistryValue['saveJob']>(input => {
    const now = new Date().toISOString();
    let saved: JobDescription;
    setState(prev => {
      const existing = input.id ? prev.jobs.find(j => j.id === input.id) : undefined;
      saved = existing ? {
        ...existing,
        ...input,
        id: existing.id,
        reference: existing.reference,
        updatedAt: now
      } : {
        ...input,
        id: newId('job'),
        reference: ref('JD'),
        createdAt: now,
        updatedAt: now
      };
      return {
        ...prev,
        jobs: existing ? prev.jobs.map(j => j.id === existing.id ? saved : j) : [saved, ...prev.jobs],
        activity: [event('job', existing ? `Job description updated — ${saved.title}` : `Job description created — ${saved.title}`, `${saved.organization || 'Client withheld'} · ${saved.reference} · ${saved.status}`), ...prev.activity].slice(0, 400)
      };
    });
    return saved!;
  }, []);
  const setJobStatus = useCallback<RegistryValue['setJobStatus']>((jobId, status) => {
    setState(prev => ({
      ...prev,
      jobs: prev.jobs.map(j => j.id === jobId ? {
        ...j,
        status,
        updatedAt: new Date().toISOString()
      } : j)
    }));
  }, []);
  const removeJob = useCallback<RegistryValue['removeJob']>(jobId => {
    setState(prev => ({
      ...prev,
      jobs: prev.jobs.filter(j => j.id !== jobId),
      assignments: prev.assignments.filter(a => a.jobId !== jobId)
    }));
  }, []);

  /* ------------------------------ Assignments ------------------------------ */

  const assignJob = useCallback<RegistryValue['assignJob']>((jobId, registrationId, note) => {
    setState(prev => {
      const already = prev.assignments.some(a => a.jobId === jobId && a.registrationId === registrationId);
      if (already) return prev;
      const candidate = prev.registrations.find(r => r.id === registrationId);
      const job = prev.jobs.find(j => j.id === jobId);
      if (!candidate || !job) return prev;
      const assignment: Assignment = {
        id: newId('asg'),
        jobId,
        registrationId,
        candidateName: candidate.fullName,
        candidateEmail: candidate.email,
        note,
        status: 'invited',
        assignedAt: new Date().toISOString()
      };
      return {
        ...prev,
        assignments: [assignment, ...prev.assignments],
        activity: [event('assignment', `${candidate.fullName} invited to apply`, `${job.title} · ${job.reference}`), ...prev.activity].slice(0, 400)
      };
    });
  }, []);
  const setAssignmentStatus = useCallback<RegistryValue['setAssignmentStatus']>((assignmentId, status) => {
    setState(prev => ({
      ...prev,
      assignments: prev.assignments.map(a => a.id === assignmentId ? {
        ...a,
        status
      } : a)
    }));
  }, []);
  const removeAssignment = useCallback<RegistryValue['removeAssignment']>(assignmentId => {
    setState(prev => ({
      ...prev,
      assignments: prev.assignments.filter(a => a.id !== assignmentId)
    }));
  }, []);
  const clearActivity = useCallback(() => setState(prev => ({
    ...prev,
    activity: []
  })), []);
  const syncFromInbox = useCallback<RegistryValue['syncFromInbox']>(async () => {
    const {
      records: inbound,
      reachable,
      error
    } = await fetchRegistrations();
    if (!reachable) {
      setLastRefreshedAt(new Date().toISOString());
      return {
        added: 0,
        found: 0,
        reachable: false,
        error
      };
    }
    {
      let added = 0;
      setState(prev => {
        const seen = new Set(prev.registrations.map(r => `${r.email.toLowerCase()}|${r.submittedAt}`));
        const seenRefs = new Set(prev.registrations.map(r => r.reference));
        const fresh: RegistrationRecord[] = [];
        for (const item of inbound) {
          const key = `${item.email.toLowerCase()}|${item.submittedAt}`;
          if (seen.has(key) || seenRefs.has(item.reference)) continue;
          seen.add(key);
          seenRefs.add(item.reference);
          fresh.push({
            id: newId('reg'),
            reference: item.reference || ref('REG'),
            fullName: item.fullName,
            email: item.email,
            professionalTitle: item.professionalTitle ?? '',
            yearsExperience: item.yearsExperience ?? '',
            linkedInUrl: item.linkedInUrl ?? '',
            motivation: item.motivation ?? '',
            submittedAt: item.submittedAt || new Date().toISOString(),
            reviewed: false
          });
        }
        added = fresh.length;
        if (!fresh.length) return prev;
        return {
          ...prev,
          registrations: [...fresh, ...prev.registrations].sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()),
          activity: [event('system', `${fresh.length} registration${fresh.length === 1 ? '' : 's'} received from other devices`, fresh.map(f => f.fullName).join(', ')), ...prev.activity].slice(0, 400)
        };
      });
      setLastRefreshedAt(new Date().toISOString());
      return {
        added,
        found: inbound.length,
        reachable: true
      };
    }
  }, []);
  const value = useMemo<RegistryValue>(() => ({
    registrations: state.registrations,
    activity: state.activity,
    threads: state.threads,
    jobs: state.jobs,
    assignments: state.assignments,
    addRegistration,
    toggleReviewed,
    removeRegistration,
    startThread,
    startOutreach,
    markDispatched,
    replyToThread,
    markThreadRead,
    setThreadStatus,
    saveJob,
    setJobStatus,
    removeJob,
    assignJob,
    setAssignmentStatus,
    removeAssignment,
    logActivity,
    clearActivity,
    refresh,
    lastRefreshedAt,
    syncFromInbox
  }), [lastRefreshedAt, refresh, syncFromInbox, addRegistration, assignJob, clearActivity, logActivity, markDispatched, markThreadRead, removeAssignment, startOutreach, removeJob, removeRegistration, replyToThread, saveJob, setAssignmentStatus, setJobStatus, setThreadStatus, startThread, state, toggleReviewed]);
  return <RegistryContext.Provider value={value} data-id="element-562" data-mp="1tc8f0j91itl6">{children}</RegistryContext.Provider>;
}
export function useRegistry(): RegistryValue {
  const ctx = useContext(RegistryContext);
  if (!ctx) throw new Error('useRegistry must be used within a RegistryProvider');
  return ctx;
}